/* eslint-disable no-underscore-dangle */
import MatchmakingService from '@/services/MatchmakingService';

export default {
  data() {
    return {
      offerDetailPromises: {},
    };
  },

  methods: {
    offerListRef() {
      const list = this[this.offerListKey];
      return Array.isArray(list) ? list : [];
    },

    offerThreadRef(offerId) {
      const refVal = this.$refs[`offerThread-${offerId}`];
      return Array.isArray(refVal) ? refVal[0] : refVal;
    },

    async scrollToOfferThread(offerId) {
      await this.$nextTick();
      const thread = this.offerThreadRef(offerId);
      if (thread && typeof thread.scrollToLastMessage === 'function') {
        await thread.scrollToLastMessage();
      }
    },

    offerMessageIds(offer) {
      return (Array.isArray(offer && offer.messages) ? offer.messages : [])
        .map((m) => Number(m && m.id))
        .filter((id) => Number.isInteger(id) && id > 0);
    },

    async markOfferMessagesRead(offer) {
      const msgIds = this.offerMessageIds(offer);
      if (!offer || !offer.id || !msgIds.length) return false;
      try {
        await MatchmakingService.markMarketplaceProductOfferMessagesRead(offer.id, msgIds);
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return false;
      }
    },

    offerIndexById(key) {
      return this.offerListRef().findIndex((o) => String(o && o.id) === String(key));
    },

    normalizeOfferDetailsResponse(raw, offerId) {
      const pickMessages = (obj) => {
        if (!obj || typeof obj !== 'object') return [];
        if (Array.isArray(obj.messages)) return obj.messages;
        if (Array.isArray(obj.message)) return obj.message;
        if (Array.isArray(obj.thread)) return obj.thread;
        return [];
      };

      let offer = null;
      let messages = [];

      if (Array.isArray(raw)) {
        offer = raw.find((o) => String(o && o.id) === String(offerId)) || raw[0] || null;
        messages = pickMessages(offer);
      } else if (raw && typeof raw === 'object') {
        const maybeOffer = raw.offer || raw.data || raw.item || raw;
        offer = maybeOffer;
        messages = pickMessages(raw);
        if (!messages.length) messages = pickMessages(maybeOffer);
      }

      if (!offer || typeof offer !== 'object') offer = { id: offerId };
      if (!Array.isArray(messages)) messages = [];

      return { offer, messages };
    },

    loadOfferDetails(offer, force = false) {
      if (!offer || !offer.id) return Promise.resolve(false);
      const key = String(offer.id);
      const idx = this.offerIndexById(key);
      if (idx === -1) return Promise.resolve(false);
      const current = this.offerListRef()[idx];
      const inFlight = this.offerDetailPromises[key];

      if (!force) {
        if (inFlight) return inFlight;
        // eslint-disable-next-line no-underscore-dangle
        if (current.__detailsLoaded) return Promise.resolve(true);
      }

      const base = inFlight ? inFlight.then(() => {}, () => {}) : Promise.resolve();
      const tracked = base
        .then(() => this.fetchOfferDetails(key))
        .then((ok) => {
          if (this.offerDetailPromises[key] === tracked) {
            this.$delete(this.offerDetailPromises, key);
          }
          return ok;
        });

      this.$set(this.offerDetailPromises, key, tracked);
      return tracked;
    },

    fetchOfferDetails(key) {
      const idx = this.offerIndexById(key);
      if (idx === -1) return Promise.resolve(false);
      const current = this.offerListRef()[idx];

      this.$set(current, '__detailsLoading', true);
      this.$set(current, '__detailsError', '');

      return MatchmakingService.getMarketplaceProductOffer(current.id)
        .then((res) => {
          const normalized = this.normalizeOfferDetailsResponse(res && res.data, current.id);
          const i = this.offerIndexById(key);
          const cur = i !== -1 ? this.offerListRef()[i] : current;
          const merged = {
            ...cur,
            ...normalized.offer,
            messages: Array.isArray(normalized.messages) ? normalized.messages : cur.messages,
            __detailsLoaded: true,
            __detailsLoading: false,
            __detailsError: '',
          };
          if (i !== -1) this.$set(this[this.offerListKey], i, merged);
          return true;
        })
        .catch((e) => {
          const i = this.offerIndexById(key);
          const cur = i !== -1 ? this.offerListRef()[i] : current;
          this.$set(cur, '__detailsLoading', false);
          this.$set(cur, '__detailsLoaded', false);
          this.$set(cur, '__detailsError', 'Failed to load offer details.');
          // eslint-disable-next-line no-console
          console.error(e);
          return false;
        });
    },
  },
};
